import { Alert, Button, Card } from '@helix-ds/react'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Navigate } from 'react-router-dom'
import type { ConsentFlowSteps } from '../../../utils/consentFlowHelper'
import { WorkflowProgress } from '../../consent/consentOptions/ConsentFlowLayout'
import { useConnectSubjects } from '../hooks/useConnectSubjects'
import type { HrcConnectSubject } from '../types'

const HEADING_ID = 'connect-records-heading'
const HOW_IT_WORKS_ID = 'connect-records-steps-heading'
const CONNECTION_STEPS = ['stepOne', 'stepTwo', 'stepThree', 'stepFour']

/** Whole sentences vary with eligibility and available names, never with unrelated family data. */
function eligibilityMessage(self: HrcConnectSubject | undefined, child: HrcConnectSubject | undefined) {
  const selfName = self?.name?.trim()
  const childName = child?.name?.trim()
  let key: string
  if (self && child) {
    if (selfName && childName) key = 'eligibleBoth'
    else if (selfName) key = 'eligibleBothSelf'
    else if (childName) key = 'eligibleBothChild'
    else key = 'eligibleBothUnnamed'
  } else if (self) {
    key = selfName ? 'eligibleSelfNamed' : 'eligibleSelf'
  } else {
    key = childName ? 'eligibleChildNamed' : 'eligibleChild'
  }
  return { id: `healthRecords.connectPage.${key}`, values: { self: selfName ?? '', child: childName ?? '' } }
}

type HealthRecordsConnectIntroductionProps = {
  workflowSteps: ConsentFlowSteps
  onContinue: () => void
}

/** Post-consent explanation shown once before handing the user to the reusable HRC page. */
const HealthRecordsConnectIntroduction: React.FC<HealthRecordsConnectIntroductionProps> = ({
  workflowSteps,
  onContinue,
}) => {
  const { load, retry } = useConnectSubjects()

  if (load.phase === 'ready' && load.subjects.length === 0) {
    return <Navigate replace to="/home" />
  }

  const subjects = load.phase === 'ready' ? load.subjects : []
  const self = subjects.find((subject) => subject.role === 'SELF')
  const child = subjects.find((subject) => subject.role === 'PROBAND')
  const childName = child?.name?.trim()
  const eligibility = eligibilityMessage(self, child)

  return (
    <div className="mx-auto flex w-full max-w-[900px] flex-col gap-8 pb-10">
      <Card padding="lg" role="region" aria-labelledby={HEADING_ID}>
        <div className="space-y-6">
          <div
            className="bg-brand-50 text-brand-600 dark:bg-brand-900 dark:text-brand-300 flex h-14 w-14 items-center justify-center rounded-full"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-8 w-8">
              <path
                d="M20 8a8 8 0 0 0-14-3L3 8m0-5v5h5M4 16a8 8 0 0 0 14 3l3-3m0 5v-5h-5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 id={HEADING_ID} className="text-brand-600 dark:text-brand-300 text-3xl font-semibold tracking-tight">
            <FormattedMessage id="healthRecords.connectPage.title" />
          </h1>

          {load.phase === 'loading' ? (
            <p role="status" className="text-text-body text-base dark:text-gray-300">
              <FormattedMessage id="healthRecords.section.loading" />
            </p>
          ) : load.phase === 'failed' ? (
            <>
              <Alert color="failure">
                <FormattedMessage id="healthRecords.section.loadFailed" />
              </Alert>
              <div className="flex justify-end">
                <Button variant="secondary" className="cursor-pointer rounded-full" onClick={retry}>
                  <FormattedMessage id="healthRecords.card.tryAgain" />
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="border-border-base bg-background-secondary rounded-xl border p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-text-body text-base leading-7 dark:text-gray-300">
                  <FormattedMessage id="healthRecords.connectPage.intro" />
                </p>
              </div>
              <section aria-labelledby={HOW_IT_WORKS_ID} className="dark:border-gray-700 dark:bg-gray-900">
                <h2 id={HOW_IT_WORKS_ID} className="text-text-heading text-lg font-semibold dark:text-white">
                  <FormattedMessage id="healthRecords.connectPage.howItWorks" />
                </h2>
                <ol aria-labelledby={HOW_IT_WORKS_ID} className="mt-4 space-y-5">
                  {CONNECTION_STEPS.map((step, index) => (
                    <li key={step} className="flex items-start gap-4">
                      <span
                        aria-hidden="true"
                        className="bg-background-secondary text-brand-600 dark:text-brand-300 flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-semibold dark:bg-gray-800"
                      >
                        {index + 1}
                      </span>
                      <p className="text-text-body pt-0.5 text-base leading-7 dark:text-gray-300">
                        <FormattedMessage id={`healthRecords.connectPage.${step}`} />
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
              <div
                role="note"
                className="bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-200 flex items-start gap-3 rounded-xl p-4"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="mt-0.5 h-6 w-6 shrink-0"
                >
                  <path d="m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z" strokeLinejoin="round" />
                  <path d="m8 12 3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="space-y-2 text-sm leading-6">
                  <p>
                    <FormattedMessage {...eligibility} />{' '}
                    <FormattedMessage id="healthRecords.connectPage.otherAdults" />
                  </p>
                  {child ? (
                    <p>
                      <FormattedMessage
                        id={`healthRecords.connectPage.proxyNotice${childName ? 'Named' : 'Child'}`}
                        values={{ name: childName ?? '' }}
                      />
                    </p>
                  ) : null}
                </div>
              </div>
              <p className="text-text-body text-base leading-7 dark:text-gray-300">
                <FormattedMessage id="healthRecords.connectPage.finishLater" />
              </p>
            </>
          )}

          <div className="flex justify-end">
            <Button
              variant="primary"
              className="cursor-pointer rounded-full"
              disabled={load.phase !== 'ready'}
              onClick={onContinue}
            >
              <FormattedMessage id="healthRecords.connectPage.continue" />
              <img src="/icons/arrow-right.svg" alt="" aria-hidden="true" className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {load.phase === 'ready' ? (
        <WorkflowProgress
          current={[
            {
              ...workflowSteps.secondary,
              titleKey: 'healthRecords.connectPage.currentTitle',
              descriptionKey: 'healthRecords.connectPage.currentDescription',
            },
          ]}
          next={[
            {
              ...workflowSteps.profile,
              titleKey: 'healthRecords.connectPage.homeTitle',
              descriptionKey: 'healthRecords.connectPage.homeDescription',
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default HealthRecordsConnectIntroduction
